<div class="mx-auto max-w-(--breakpoint-2xl) px-3">
  <!-- TVG-236: return to the Sales Dashboard with the worklist filters restored. -->
  @if (showBackToDashboard) {
    <button
      type="button"
      class="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 transition-colors hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
      (click)="backToDashboard()"
    >
      <lucide-icon [name]="ArrowLeftIcon" class="h-4 w-4"></lucide-icon>
      Back to Dashboard
    </button>
  }
  <div class="w-full">
    <div class="relative my-6 flex items-center justify-between gap-3">
      <h2 class="text-2xl font-medium text-gray-800 dark:text-white/90">
        @if (isCreating) {
          Add Contact
        } @else {
          {{ user.name }}
        }
      </h2>
      <!-- TVG-237: staff help panel trigger -->
      <app-help-panel page="contact"></app-help-panel>
    </div>
  </div>

  <form [formGroup]="form">

    <div class="flex gap-3 items-start">
      <div class="w-full min-w-0">

        <!-- TVG-360: the contact block. Every field in one compact grid, four
             across on a wide screen, with Edit and Save in the header — the pair
             staff actually use, since the page is long. The fields are read only
             until Edit is pressed. -->
        <div class="mb-3 rounded-2xl w-full border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]" data-testid="contact-block">

          <div class="flex flex-wrap items-center justify-between gap-3 px-5 py-4 sm:px-6 sm:py-5">
            <h3 class="text-base font-medium text-gray-800 dark:text-white/90">
              Contact
            </h3>
            @if (!isCreating) {
              <div class="flex flex-wrap items-center gap-3">
                @if (!editing) {
                  <span class="text-theme-xs text-gray-500 dark:text-gray-400">Fields are read only until you press Edit</span>
                }
                @if (canEditContact) {
                  <app-button
                    [label]="'Edit'"
                    [style]="'secondary'"
                    [disabled]="editing"
                    data-testid="contact-block-edit"
                    (click)="startEditing()"
                  ></app-button>
                  <app-button
                    [label]="'Save'"
                    [style]="'primary'"
                    [disabled]="!editing || processing || !form.valid || form.pristine"
                    data-testid="contact-block-save"
                    (click)="saveAndContinue()"
                  ></app-button>
                }
              </div>
            }
          </div>

          <div class="border-t border-gray-100 p-5 dark:border-gray-800 sm:p-6">
            <div class="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              <div>
                <app-text-input [control]="form.controls.email" [type]="'email'" [name]="'email'" [placeholder]="'user@example.com'" [label]="'Email'" [disabled]="!editing"></app-text-input>
              </div>
              <div>
                <app-text-input [control]="form.controls.name" [name]="'name'" [placeholder]="'User Name'" [label]="'Name'" [disabled]="!editing"></app-text-input>
              </div>
              <!-- TVG-322: email and name are still all a new contact has to
                   have. Beside them the creation form offers the address, the
                   lead source and the corporate tick box, so whatever staff
                   already know goes on in one go. The rest of the contact's
                   detail is still added afterwards, once it exists. -->
              @if (isCreating) {
                <div class="sm:col-span-2">
                  <app-text-input [control]="form.controls.address_line_1" [type]="'text'" [name]="'address_line_1'" [placeholder]="'Address (optional)'" [label]="'Address'"></app-text-input>
                </div>
                <div>
                  <app-select [control]="form.controls.source" [choices]="leadSourceChoices" label="Lead Source" [searchable]="true"></app-select>
                </div>
                @if (sourceIsOther) {
                  <div>
                    <app-text-input [control]="form.controls.source_other" [type]="'text'" [name]="'source_other'" [placeholder]="'Where did they hear about us?'" [label]="'Lead Source Detail'"></app-text-input>
                  </div>
                }
                <div data-testid="contact-corporate-create">
                  <p class="mb-1 text-theme-xs text-gray-500 dark:text-gray-400">Corporate client</p>
                  <div class="flex min-h-11 items-center py-1">
                    <app-checkbox [control]="form.controls.is_corporate" [label]="isCorporateClient ? 'Yes' : 'No'" [inline]="true"></app-checkbox>
                  </div>
                </div>
                @if (isCorporateClient) {
                  <div>
                    <app-text-input [control]="form.controls.company_name" [type]="'text'" [name]="'company_name'" [placeholder]="'Company name'" [label]="'Company name'"></app-text-input>
                  </div>
                }
              }

              @if (!isCreating) {
                <div>
                  <app-array-input [formArray]="form.controls.phone_numbers" [type]="'text'" [name]="'phone_numbers'" [label]="'Phone'" [placeholder]="'Phone'" [static]="!editing"></app-array-input>
                </div>
                <!-- TVG-312: lead source, from the admin-managed list on Admin →
                     Users. Retired sources are not offered here but stay on
                     contacts that already hold them. -->
                <div>
                  <app-select [control]="form.controls.source" [choices]="leadSourceChoices" label="Lead Source" [searchable]="true"></app-select>
                </div>
                @if (sourceIsOther) {
                  <div>
                    <app-text-input [control]="form.controls.source_other" [type]="'text'" [name]="'source_other'" [placeholder]="'Where did they hear about us?'" [label]="'Lead Source Detail'" [disabled]="!editing"></app-text-input>
                  </div>
                }

                <!-- The address is one line with its city and postcode beside it,
                     not three full-width rows of its own. -->
                <div class="sm:col-span-2">
                  <app-text-input [control]="form.controls.address_line_1" [type]="'text'" [name]="'address_line_1'" [placeholder]="'Address Line 1'" [label]="'Address'" [disabled]="!editing"></app-text-input>
                </div>
                <div>
                  <app-text-input [control]="form.controls.city" [type]="'text'" [name]="'city'" [placeholder]="'City'" [label]="'City'" [disabled]="!editing"></app-text-input>
                </div>
                <div>
                  <app-text-input [control]="form.controls.postcode" [type]="'text'" [name]="'postcode'" [placeholder]="'Postcode'" [label]="'Postcode'" [disabled]="!editing"></app-text-input>
                </div>
                <div class="sm:col-span-2">
                  <app-text-input [control]="form.controls.address_line_2" [type]="'text'" [name]="'address_line_2'" [placeholder]="'Address Line 2 (optional)'" [label]="'Address Line 2'" [disabled]="!editing"></app-text-input>
                </div>
                <div>
                  <app-text-input [control]="form.controls.country" [type]="'text'" [name]="'country'" [placeholder]="'Country'" [label]="'Country'" [disabled]="!editing"></app-text-input>
                </div>
                <!-- TVG-322: the second email was never used and is gone from
                     the contact pages. -->

                <div>
                  <app-select [control]="form.controls.contact_status" [choices]="contactStatusChoices" label="Status" [searchable]="true"></app-select>
                </div>

                <!-- TVG-360: corporate client sits beside Status. Ticking it
                     brings the company name field to life next to it; clearing
                     it empties the company name again. -->
                <div data-testid="contact-corporate">
                  <p class="mb-1 text-theme-xs text-gray-500 dark:text-gray-400">Corporate client</p>
                  <div
                    class="flex min-h-11 items-center py-1"
                    [class.pointer-events-none]="!editing"
                    [class.opacity-70]="!editing">
                    <app-checkbox [control]="form.controls.is_corporate" [label]="isCorporateClient ? 'Yes' : 'No'" [inline]="true"></app-checkbox>
                  </div>
                </div>

                @if (isCorporateClient) {
                  <div>
                    <app-text-input [control]="form.controls.company_name" [type]="'text'" [name]="'company_name'" [placeholder]="'Company name'" [label]="'Company name'" [disabled]="!editing"></app-text-input>
                  </div>
                }

                <!-- TVG-315/TVG-360: the three consent permissions sit inside the
                     contact block, on one line. They are self explanatory, so the
                     separate panel and its description are gone. Nothing here
                     sends anything — these are the permissions a future send has
                     to respect. -->
                <div class="sm:col-span-2" data-testid="contact-permissions">
                  <p class="mb-1 text-theme-xs text-gray-500 dark:text-gray-400">Permissions</p>
                  <div
                    class="flex min-h-11 flex-wrap items-center gap-x-6 gap-y-2 py-1"
                    [class.pointer-events-none]="!editing"
                    [class.opacity-70]="!editing">
                    <app-checkbox [control]="form.controls.email_permission" [label]="'Email'" [inline]="true"></app-checkbox>
                    <app-checkbox [control]="form.controls.text_permission" [label]="'Text'" [inline]="true"></app-checkbox>
                    <app-checkbox [control]="form.controls.marketing_permission" [label]="'Marketing'" [inline]="true"></app-checkbox>
                  </div>
                  <!-- TVG-344: on an existing contact, a change here is consent
                       taken by phone and is stored with the phone wording. -->
                  @if (editing && user.id) {
                    <p class="mt-1 text-theme-xs text-gray-500 dark:text-gray-400" data-testid="contact-permissions-phone-hint">
                      Changing a box here records consent taken by phone, with the wording shown under Consent history.
                    </p>
                  }
                </div>

                @if (editing) {
                  <div>
                    <app-text-input [control]="form.controls.password" [type]="'password'" [name]="'password'" [placeholder]="'***'" [label]="'Password'"></app-text-input>
                  </div>
                }

                <div class="sm:col-span-2 lg:col-span-3 xl:col-span-4">
                  @if (editing) {
                    <app-searchable-select
                      [control]="form.controls.event_ids"
                      [multiple]="true"
                      [searchFunction]="eventSearchFunction"
                      [initialOptions]="initialEventChoices"
                      [placeholder]="'Select Events'"
                      [searchPlaceholder]="'Search events...'"
                      [minSearchLength]="2"
                      [height]="'11'"
                      label="Events">
                    </app-searchable-select>
                  } @else {
                    <p class="mb-1 text-theme-xs text-gray-500 dark:text-gray-400">Events</p>
                    @if (eventChips.length) {
                      <div class="flex flex-wrap gap-1.5">
                        @for (chip of eventChips; track chip) {
                          <span class="inline-flex items-center rounded-full bg-brand-100 px-2.5 py-1 text-theme-xs font-medium text-brand-800 dark:bg-brand-500/20 dark:text-brand-300">
                            {{ chip }}
                          </span>
                        }
                      </div>
                    } @else {
                      <p class="text-theme-sm text-gray-500 dark:text-gray-400">No events linked to this contact yet.</p>
                    }
                  }
                </div>
              }
            </div>

            <!-- TVG-318: before the contact is created, show anybody already on
                 file who matches the name or email so staff open them instead
                 of creating a duplicate. -->
            @if (isCreating) {
              <div class="mt-6 border-t border-gray-100 pt-5 dark:border-gray-800">
                <div class="flex items-center gap-2">
                  <h4 class="text-sm font-medium text-gray-700 dark:text-gray-400">Existing contacts</h4>
                  @if (searchingExisting) {
                    <span class="text-theme-xs text-gray-500 dark:text-gray-400">Searching…</span>
                  }
                </div>

                @if (existingMatches.length) {
                  @if (duplicateEmailMatch) {
                    <p class="mt-2 text-theme-sm text-error-500">
                      A contact with this email address already exists. Open it instead of creating a new one.
                    </p>
                  } @else {
                    <p class="mt-2 text-theme-sm text-gray-500 dark:text-gray-400">
                      These contacts already exist. Open one if it is the same person.
                    </p>
                  }

                  <ul class="mt-3 divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 dark:divide-gray-800 dark:border-gray-800">
                    @for (match of existingMatches; track match.id) {
                      <li class="flex items-center justify-between gap-3 px-4 py-3">
                        <div class="min-w-0">
                          <p class="truncate text-theme-sm font-medium text-gray-800 dark:text-white/90">{{ match.name }}</p>
                          <p class="truncate text-theme-xs text-gray-500 dark:text-gray-400">{{ match.email }}</p>
                        </div>
                        <app-button
                          [label]="'Open'"
                          [style]="'secondary'"
                          (click)="openExistingContact(match.id)"
                        ></app-button>
                      </li>
                    }
                  </ul>
                } @else if (!searchingExisting) {
                  <p class="mt-2 text-theme-sm text-gray-500 dark:text-gray-400">
                    No existing contact matches this name or email yet.
                  </p>
                }
              </div>
            }
          </div>
        </div>

        @if (user.id) {
          <!-- TVG-360 section eleven (a and b): the contact's own sales journey,
               shown once directly under the contact block and above show rounds,
               with the venues they are booked to view named beneath it. Both are
               fed by the panels further down the page, so nothing is fetched
               twice. -->
          @if (crmSalesFlowEnabled) {
            <app-contact-journey-summary
              [journey]="contactJourney"
              [showRounds]="contactShowRounds"
            ></app-contact-journey-summary>
          }

          <!-- TVG-324/TVG-360/TVG-368: every viewing booked across this
               contact's events, sitting directly under the contact block.
               The heading and the Book a viewing button live inside the
               component itself; the rounds stay booked on their events —
               this lists and starts them. -->
          @if (crmSalesFlowEnabled) {
            <app-contact-show-rounds
              [contactId]="user.id!"
              [canCreateEvents]="canCreateEvents"
              (showRoundsLoaded)="contactShowRounds = $event"
              (bookViewing)="openBookShowRound()"
            ></app-contact-show-rounds>
          }

          <!-- TVG-174/TVG-360: this contact's events and quotes, one line each.
               The single source of both on this page. Hidden when the toggle is
               off so toggle-off stays byte-identical to the legacy contact UI. -->
          @if (crmSalesFlowEnabled) {
            <app-contact-journey
              [contactId]="user.id"
              [canCreateEvent]="showCreateEventButton"
              (bookViewing)="openBookShowRound($event)"
              (journeyLoaded)="contactJourney = $event"
            ></app-contact-journey>
          }

          <!-- TVG-313/TVG-343/TVG-360: every email and text this contact has
               had, in both directions, with the two send buttons in its header. -->
          <!-- TVG-344: when, where and with what wording each consent was
               given or changed, and whether a text would be refused. -->
          <app-contact-consent-history
            [contactId]="user.id!"
            [stateKey]="consentStateKey"
          ></app-contact-consent-history>

          <app-contact-email-addresses
            [contactId]="user.id!"
            [canEdit]="canEditContact"
            (filed)="sentEmailsPanel?.reload()"
          ></app-contact-email-addresses>

          <app-contact-sent-emails
            [contactId]="user.id!"
            [contactName]="user.name"
            [contactEmail]="user.email"
            [contactEmailPermission]="user.email_permission !== false"
            [contactPhoneNumbers]="user.phone_numbers || []"
            [contactTextPermission]="user.text_permission !== false"
          ></app-contact-sent-emails>

          <!-- Notes Section -->
          <div class="mb-3 rounded-2xl w-full border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
            <div class="px-5 py-4 sm:px-6 sm:py-5">
              <h3 class="text-base font-medium text-gray-800 dark:text-white/90">
                Notes
              </h3>
            </div>
            <div class="p-5 space-y-6 border-t border-gray-100 dark:border-gray-800 sm:p-6">
              @if (user.notes && user.notes.length > 0) {
                <div class="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
                  <table class="w-full text-left text-theme-sm">
                    <thead class="bg-gray-100 text-theme-xs text-gray-500 dark:bg-white/[0.04] dark:text-gray-400">
                    <tr>
                      <th class="w-[22%] px-4 py-2.5 font-medium">Created</th>
                      <th class="px-4 py-2.5 font-medium">Note</th>
                      @if (editing) {
                        <th class="w-[5%] px-4 py-2.5 font-medium">Edit</th>
                        <th class="w-[5%] px-4 py-2.5 font-medium">Delete</th>
                      }
                    </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100 dark:divide-gray-800">
                      @for (note of user.notes; track note.id) {
                        <tr class="text-gray-700 dark:text-gray-300">
                          <td class="px-4 py-2.5">
                            {{ note.created_at | date: 'dd/MM/yyyy HH:mm' }} by {{ note.created_by_username }}
                          </td>
                          <td class="px-4 py-2.5">
                            <span [innerHtml]="note.note"></span>
                          </td>
                          @if (editing) {
                            <td class="px-4 py-2.5">
                              <button (click)="updateNote(note.id)" class="text-gray-500 hover:text-primary-500 dark:text-gray-400 dark:hover:text-primary-500">
                                <lucide-icon [size]="18" [name]="EditIcon"></lucide-icon>
                              </button>
                            </td>
                            <td class="px-4 py-2.5">
                              <button (click)="deleteNote(note.id)" class="text-gray-500 hover:text-error-500 dark:text-gray-400 dark:hover:text-error-500">
                                <lucide-icon [size]="18" [name]="TrashIcon"></lucide-icon>
                              </button>
                            </td>
                          }
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              }

              @if (!showNoteForm && editing) {
                <div class="w-full mt-3 flex justify-end gap-3">
                  <app-button [label]="'Add Note'" (click)="showNoteForm = true"></app-button>
                </div>
              }

              @if (noteForm && showNoteForm) {
                <div class="grid grid-cols-1 gap-5 mt-5">
                  <div>
                    @if (editor) {
                      <div class="NgxEditor__Wrapper min-h-40 h-auto">
                        <ngx-editor-menu [editor]="editor" [toolbar]="toolbar"></ngx-editor-menu>
                        <ngx-editor
                          [formControl]="noteForm.controls.note"
                          [editor]="editor"
                          [placeholder]="'Type here...'"
                        ></ngx-editor>
                      </div>
                    }
                  </div>
                  <div class="w-full mt-3 flex justify-end gap-3">
                    <app-button [label]="'Cancel'" [disabled]="processingNote" [style]="'secondary'" (click)="cancelNote()"></app-button>
                    <app-button [label]="editNoteId ? 'Update Note' : 'Save Note'" [disabled]="processingNote || !noteForm.valid || noteForm.pristine"
                                (click)="saveNote()"></app-button>
                  </div>
                </div>
              }

              <app-modal
                [title]="'Delete Note'"
                [content]="'Are you sure you want to delete this note?'"
                [show]="isDeleteModalOpen"
                [closeButtonLabel]="'Cancel'"
                [confirmButtonLabel]="'Yes, Proceed'"
                (onClose)="isDeleteModalOpen = false"
                (onConfirm)="confirmDelete()"
              ></app-modal>
            </div>
          </div>

          <!-- TVG-359: the counterpart to the sent list above — what the platform
               still plans to send, when, and why anything was stopped. Read
               only; the panel offers no cancel and no send. -->
          <div class="mb-3 rounded-2xl w-full border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
            <div class="px-5 py-4 sm:px-6 sm:py-5">
              <h3 class="text-base font-medium text-gray-800 dark:text-white/90">Queued emails</h3>
            </div>
            <div class="p-5 space-y-6 border-t border-gray-100 dark:border-gray-800 sm:p-6">
              <app-contact-scheduled-emails [contactId]="user.id!"></app-contact-scheduled-emails>
            </div>
          </div>

          <!-- TVG-359: pre-production only, and only for a seeded fixture
               contact. The component hides itself unless the server says both
               are true, so this renders nothing on the live site. -->
          <app-contact-qa-tools [contactEmail]="user.email"></app-contact-qa-tools>
        } @else {
          <div class="mb-3 rounded-2xl w-full border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
            <div class="px-5 py-4 sm:px-6 sm:py-5">
              <h3 class="text-base font-medium text-gray-800 dark:text-white/90">
                Notes
              </h3>
            </div>
            <div class="p-5 space-y-6 border-t border-gray-100 dark:border-gray-800 sm:p-6">
              <p class="text-gray-500 dark:text-gray-400">Please save the contact to add notes.</p>
            </div>
          </div>
        }
      </div>
    </div>

    <!-- The page is long, so the save pair at the top is the one staff reach
         for. This one stays for anybody who has read to the bottom. -->
    <div class="mt-8 flex flex-row items-center justify-end gap-3">
      @if (!editing) {
        <app-button
          [label]="'Back'"
          [icon]="XIcon"
          [style]="'secondary'"
          (click)="cancel()"
        ></app-button>

        @if (canEditContact) {
          <app-button
            [label]="'Edit'"
            [icon]="EditIcon"
            [style]="'primary'"
            (click)="startEditing()"
          ></app-button>
        }
      } @else {
        <app-button
          [label]="'Cancel'"
          [icon]="XIcon"
          [style]="'secondary'"
          [disabled]="processing"
          (click)="cancel()"
        ></app-button>

        <app-button
          [label]="'Save & Continue'"
          [icon]="CheckCircleIcon"
          [style]="'secondary'"
          [disabled]="processing || !form.valid || form.pristine || !!duplicateEmailMatch"
          (click)="saveAndContinue()"
        ></app-button>

        <app-button
          [label]="'Save'"
          [icon]="CheckCircleIcon"
          [style]="'primary'"
          [disabled]="processing || !form.valid || form.pristine || !!duplicateEmailMatch"
          (click)="save()"
        ></app-button>
      }
    </div>

  </form>
</div>

<!-- TVG-360/TVG-331: book a show round without leaving the contact page. The
     dialog only chooses what to book; the booking itself goes through the same
     endpoint the Deals board uses, so a round is saved on its event in exactly
     one way. -->
@if (crmSalesFlowEnabled && canCreateEvents) {
  <app-book-new-viewing-modal
    [isOpen]="bookShowRoundOpen"
    [submitting]="bookShowRoundSubmitting"
    [contacts]="bookableContacts"
    [loading]="bookShowRoundLoading"
    (confirmed)="submitBookShowRound($event)"
    (cancelled)="closeBookShowRound()"
  ></app-book-new-viewing-modal>
}
